import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Play, Download, RefreshCw, XCircle } from 'lucide-react';
import { useProject, useProjectStatus } from '../hooks/useProject';
import Card, { CardHeader } from '../components/common/Card';
import Button from '../components/common/Button';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { project, isLoading, error, startGeneration, cancelProject } = useProject(id!);
  const status = useProjectStatus(id!, 3000);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-jelly-500"></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Project not found'}</p>
        <Link to="/admin/projects" className="text-jelly-400 hover:text-jelly-300 mt-4 inline-block">
          Back to projects
        </Link>
      </div>
    );
  }

  const currentStatus = status?.status || project.status;
  const progress = status?.progress || 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link to="/admin/projects" className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200">
        <ArrowLeft className="w-4 h-4" />
        Back to projects
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold capitalize">{project.target_platform} Video Project</h1>
          <p className="text-zinc-400 mt-1">
            {project.target_duration}s • {project.quality_tier} quality
          </p>
        </div>
        <div className="flex gap-2">
          {currentStatus === 'pending' && <Button onClick={startGeneration}>Start Generation</Button>}
          {['composing', 'generating', 'processing'].includes(currentStatus) && (
            <Button variant="danger" onClick={cancelProject}>
              <XCircle className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          )}
          {currentStatus === 'complete' && project.output_url && (
            <a href={project.output_url} download>
              <Button>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Video preview */}
      <Card padding="none" className="overflow-hidden">
        <div className="aspect-[9/16] max-h-[600px] bg-zinc-950 flex items-center justify-center">
          {currentStatus === 'complete' && project.output_url ? (
            <video src={project.output_url} controls className="w-full h-full object-contain" />
          ) : ['composing', 'generating', 'processing'].includes(currentStatus) ? (
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full border-4 border-jelly-500 border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium capitalize">{currentStatus}...</p>
              <p className="text-zinc-400 mt-1">{progress}% complete</p>
              <div className="w-48 h-2 bg-zinc-800 rounded-full mt-4 mx-auto overflow-hidden">
                <div
                  className="h-full bg-jelly-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : currentStatus === 'failed' ? (
            <div className="text-center p-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-red-400">Generation Failed</p>
              <p className="text-zinc-400 mt-1">Something went wrong</p>
              <Button className="mt-4" onClick={startGeneration}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : (
            <div className="text-center p-8">
              <Play className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">Ready to generate</p>
              <Button className="mt-4" onClick={startGeneration}>
                Start Generation
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Configuration" />
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-zinc-400">Platform</span>
              <span className="capitalize">{project.target_platform}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Duration</span>
              <span>{project.target_duration} seconds</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Quality</span>
              <span className="capitalize">{project.quality_tier}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Status</span>
              <span className="capitalize">{currentStatus}</span>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Cost" />
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-zinc-400">Estimated</span>
              <span>${project.estimated_cost_usd?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Actual</span>
              <span className="font-medium">${project.actual_cost_usd?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Created</span>
              <span>{new Date(project.created_at).toLocaleString()}</span>
            </div>
            {project.completed_at && (
              <div className="flex justify-between">
                <span className="text-zinc-400">Completed</span>
                <span>{new Date(project.completed_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
